import { useState } from 'react';
import {
    Modal, ModalDialog, ModalClose, ModalOverflow,
    Typography, Stack, Box, Button, Slider, Alert, Chip,
} from '@mui/joy';
import { Snowflake, Warning } from '@phosphor-icons/react';
import { APIFreezeSubscription } from '../../api';

const MIN_DAYS = 7;
const MAX_DAYS = 30;

export default function FreezeModal({ open, onClose, token, expiresAt, onFrozen }) {
    const [days, setDays] = useState(14);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleFreeze = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await APIFreezeSubscription(token, days);
            if (data?.success) {
                onFrozen?.(data);
                onClose();
            } else {
                setError(data?.error || 'Не удалось активировать заморозку');
            }
        } catch (e) {
            setError(e?.response?.data?.error || 'Ошибка при активации заморозки');
        } finally {
            setLoading(false);
        }
    };

    const today = new Date();
    const freezeEnd = new Date(today);
    freezeEnd.setDate(freezeEnd.getDate() + days);
    const fmt = (d) => d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

    return (
        <Modal open={open} onClose={onClose}>
            <ModalOverflow>
                <ModalDialog
                    size="md"
                    sx={{
                        maxWidth: 480,
                        width: { xs: '95vw', sm: '90vw' },
                        borderRadius: 16,
                    }}
                >
                    <ModalClose />

                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: 10,
                            bgcolor: 'rgba(45, 122, 179, 0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Snowflake size={22} weight="bold" color="#2D7AB3" />
                        </Box>
                        <Box>
                            <Typography level="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                Заморозить подписку
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Бонус для подписчиков закрытого класса
                            </Typography>
                        </Box>
                    </Stack>

                    <Box sx={{
                        mt: 2, p: 2, borderRadius: 12,
                        bgcolor: 'background.body',
                        border: '1px solid', borderColor: 'divider',
                    }}>
                        <Typography level="body-sm" sx={{ mb: 1.5 }}>
                            На сколько дней заморозить?
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                            <Slider
                                value={days}
                                min={MIN_DAYS}
                                max={MAX_DAYS}
                                step={1}
                                onChange={(_, v) => setDays(v)}
                                sx={{ flex: 1 }}
                            />
                            <Chip variant="solid" color="primary" sx={{ minWidth: 60, justifyContent: 'center' }}>
                                {days} дн.
                            </Chip>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>мин. {MIN_DAYS}</Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>макс. {MAX_DAYS}</Typography>
                        </Stack>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Stack spacing={0.75}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Сегодня</Typography>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>{fmt(today)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Разморозка</Typography>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>{fmt(freezeEnd)}</Typography>
                            </Stack>
                            {expiresAt && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Подписка продлится до</Typography>
                                    <Typography level="body-sm" sx={{ fontWeight: 600, color: '#0F7B6C' }}>
                                        +{days} дн.
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Box>

                    <Alert color="warning" variant="soft" startDecorator={<Warning size={18} weight="fill" />} sx={{ mt: 2, alignItems: 'flex-start' }}>
                        <Box>
                            <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                                Заморозка доступна один раз за всю подписку
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                Самостоятельно разморозить нельзя. На время заморозки доступ к тестам и модулям закрыт. Срок подписки автоматически продлевается на выбранное количество дней.
                            </Typography>
                        </Box>
                    </Alert>

                    {error && <Alert color="danger" sx={{ mt: 1.5 }}>{error}</Alert>}

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button variant="plain" color="neutral" onClick={onClose}>
                            Отмена
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            loading={loading}
                            onClick={handleFreeze}
                            sx={{ flex: 1, fontWeight: 700 }}
                        >
                            Заморозить на {days} дн.
                        </Button>
                    </Stack>
                </ModalDialog>
            </ModalOverflow>
        </Modal>
    );
}
